import type {
  AccessLevelProp,
  BadgeGroupItemProps,
  CalloutProps,
  IconName,
  IconProps,
  ShortcutProps,
} from '@meterup/atto';
import type { To } from 'react-router';
import {
  AccessLevel,
  AlignStack,
  Badge,
  BadgeGroup,
  BodySansSizes,
  Callout,
  colors,
  containers,
  darkThemeSelector,
  devices,
  FeatureBadge,
  Icon,
  selectors,
  shadows,
  Shortcut,
  stateZIndexes,
  styled,
  Text,
  Tooltip,
  useAccessLevelContext,
  useCanHover,
} from '@meterup/atto';
import {
  type ElementType,
  type EventHandler,
  type HTMLAttributes,
  type MouseEvent,
  type MouseEventHandler,
  type ReactNode,
  type RefObject,
  type SyntheticEvent,
} from 'react';
import { mergeRefs } from 'react-merge-refs';

import { type SigilProps, Sigil } from '../../Sigil';
import { useSidebarContext } from './utils';
import { sidebarVars } from './vars';

const sidebarTargetMediaVars = {
  size: {
    minimized: {
      mouse: 16,
      touch: 18,
    },
    maximized: {
      mouse: 14,
      touch: 16,
    },
    narrow: 18,
  },
};

type SidebarTargetMediaProps = {
  icon?: IconProps;
  sigil?: Omit<SigilProps, 'size'>;
  minimized?: boolean;
};

const SidebarTargetMediaSigil = styled(Sigil, {
  variants: {
    minimized: {
      true: {
        [devices.mouse]: {
          size: `$${sidebarTargetMediaVars.size.minimized.mouse + 2}`,
        },

        [devices.touch]: {
          size: `$${sidebarTargetMediaVars.size.minimized.touch + 2}`,
        },
      },
      false: {
        [devices.mouse]: {
          size: `$${sidebarTargetMediaVars.size.maximized.mouse + 2}`,
        },

        [devices.touch]: {
          size: `$${sidebarTargetMediaVars.size.maximized.touch + 2}`,
        },

        [containers.application.narrow]: {
          size: `$${sidebarTargetMediaVars.size.narrow + 2}`,
        },
      },
    },
  },
});
SidebarTargetMediaSigil.displayName = 'SidebarTargetMediaSigil';

const SidebarTargetMediaIcon = styled(Icon, {
  display: 'flex',

  variants: {
    minimized: {
      true: {
        [devices.mouse]: {
          size: `$${sidebarTargetMediaVars.size.minimized.mouse}`,
        },

        [devices.touch]: {
          size: `$${sidebarTargetMediaVars.size.minimized.touch}`,
        },
      },
      false: {
        [devices.mouse]: {
          size: `$${sidebarTargetMediaVars.size.maximized.mouse}`,
        },

        [devices.touch]: {
          size: `$${sidebarTargetMediaVars.size.maximized.touch}`,
        },

        [containers.application.narrow]: {
          size: `$${sidebarTargetMediaVars.size.narrow}`,
        },
      },
    },
  },
});
SidebarTargetMediaIcon.displayName = 'SidebarTargetMediaIcon';

const SidebarTargetMediaContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '$$sidebarTargetMediaColor',

  variants: {
    minimized: {
      true: {
        [devices.mouse]: {
          widthAll: '$20',
        },

        [devices.touch]: {
          widthAll: '$24',
        },
      },
      false: {
        [devices.mouse]: {
          widthAll: '$14',
        },

        [devices.touch]: {
          widthAll: '$18',
        },
      },
    },
  },
});
SidebarTargetMediaContainer.displayName = 'SidebarTargetMediaContainer';

function SidebarTargetMedia({ icon, sigil, minimized }: SidebarTargetMediaProps) {
  let rendered = null;
  if (sigil) {
    rendered = <SidebarTargetMediaSigil minimized={minimized} {...sigil} />;
  }
  if (icon) {
    rendered = <SidebarTargetMediaIcon minimized={minimized} {...icon} />;
  }

  return (
    <SidebarTargetMediaContainer minimized={minimized}>{rendered}</SidebarTargetMediaContainer>
  );
}

type SidebarTargetAnnotationsBadgesProps = {
  accessLevel?: AccessLevelProp | null;
  badges?: BadgeGroupItemProps[];
  bubbled?: boolean;
};

const SidebarTargetAnnotationsBadgesContainer = styled(BadgeGroup, {
  variants: {
    bubbled: {
      true: {
        position: 'absolute',
        zIndex: stateZIndexes.fixture,
        top: '$6',
        right: '$6',
        transform: 'translate(50%, -50%)',
      },
      false: {},
    },
  },
});
SidebarTargetAnnotationsBadgesContainer.displayName = 'SidebarTargetAnnotationsBadgesContainer';

function SidebarTargetAnnotationsBadges({
  accessLevel: accessLevelProp,
  badges,
  bubbled = false,
}: SidebarTargetAnnotationsBadgesProps) {
  const accessLevel = useAccessLevelContext(accessLevelProp);

  const hasBadges = Boolean(badges?.length || accessLevel);

  if (!hasBadges) return null;

  return (
    <SidebarTargetAnnotationsBadgesContainer size="x-small" bubbled={bubbled}>
      {badges?.map((badge, index) => (
        <Badge key={`${String(badge.icon ?? 'badge')}-${String(index)}`} {...badge} />
      ))}
      {accessLevel && <FeatureBadge type={accessLevel} size="x-small" />}
    </SidebarTargetAnnotationsBadgesContainer>
  );
}

type SidebarTargetAnnotationsProps = {
  accessLevel?: AccessLevelProp | null;
  badges?: BadgeGroupItemProps[];
  shortcut?: ShortcutProps;
};

const SidebarTargetAnnotationsContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: `$${sidebarVars.gap}`,
});
SidebarTargetAnnotationsContainer.displayName = 'SidebarTargetAnnotationsContainer';

function SidebarTargetAnnotations({
  accessLevel: accessLevelProp,
  badges,
  shortcut,
}: SidebarTargetAnnotationsProps) {
  const accessLevel = useAccessLevelContext(accessLevelProp);

  return (
    <SidebarTargetAnnotationsContainer>
      {shortcut && <Shortcut {...shortcut} />}
      <SidebarTargetAnnotationsBadges accessLevel={accessLevel} badges={badges} />
    </SidebarTargetAnnotationsContainer>
  );
}

type SidebarTargetArrowsTypeProp = 'dropdown' | 'toggle';

type SidebarTargetArrowsProps = {
  isOpen?: boolean;
  type?: SidebarTargetArrowsTypeProp;
};

const SidebarTargetArrowsContainer = styled(Icon, {
  size: '$10',
  color: '$$sidebarTargetArrowsColor',

  [devices.mouse]: {
    size: '$10',
  },

  [devices.touch]: {
    size: '$12',
  },
});
SidebarTargetArrowsContainer.displayName = 'SidebarTargetArrowsContainer';

function SidebarTargetArrows({ type = 'dropdown', isOpen }: SidebarTargetArrowsProps) {
  let icon: IconName = 'chevron-down';
  if (type === 'toggle') {
    icon = 'chevron-right';
  }
  if (type === 'toggle' && isOpen) {
    icon = 'chevron-down';
  }
  return <SidebarTargetArrowsContainer icon={icon} />;
}

export type SidebarTargetProps = {
  accessLevel?: AccessLevelProp | null;
  annotations?: SidebarTargetAnnotationsProps;
  arrangement?: 'hidden-label' | 'leading-icon';
  arrows?: SidebarTargetArrowsTypeProp;
  callout?: Omit<CalloutProps, 'position'>;
  id?: string;
  internal?: boolean;
  isOpen?: boolean;
  label: ReactNode;
  media?: SidebarTargetMediaProps;
  minimized?: boolean;
  /** Merged onto the root container (e.g. Base UI `NavigationMenu.Trigger` render props). */
  composeProps?: HTMLAttributes<HTMLElement> & { ref?: React.Ref<HTMLElement | null> };
  /** When minimized, skip the label tooltip (e.g. when a flyout provides branch links). */
  suppressMinimizedTooltip?: boolean;
  /**
   * On narrow layouts, the slide-out menu stays open after activating this link so the user can
   * pick a destination in a nested sidebar (e.g. Settings / Toolshed roots).
   */
  keepMenuOpenOnNavigate?: boolean;
  selected?: boolean;
  touchDisabled?: boolean;
  // Routing & interaction
  as?: ElementType;
  onClick?: MouseEventHandler<HTMLElement>;
  ref?: RefObject<HTMLDivElement | null>;
  to?: To;
};

function mergeHandler<E extends SyntheticEvent>(
  a: EventHandler<E> | undefined,
  b: EventHandler<E> | undefined,
): EventHandler<E> | undefined {
  if (!a) return b;
  if (!b) return a;
  return (event: E) => {
    a(event);
    if (!event.isPropagationStopped()) {
      b(event);
    }
  };
}

const SidebarTargetLabel = styled(Text, {
  display: 'block',
  minWidth: 0,
  fontWeight: '$bold',
  color: '$$sidebarTargetLabelColor',
  ...BodySansSizes,
});
SidebarTargetLabel.displayName = 'SidebarTargetLabel';

const SidebarTargetEnd = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: `$${sidebarVars.gap}`,
});
SidebarTargetEnd.displayName = 'SidebarTargetEnd';

const SidebarTargetStack = styled(AlignStack, {
  position: 'relative',

  variants: {
    minimized: {
      true: {
        [devices.mouse]: {
          padding: '$4 0',
        },

        [devices.touch]: {
          padding: '$6 0',
        },
      },
      false: {
        [devices.mouse]: {
          padding: '$4 $8',
        },

        [devices.touch]: {
          padding: '$6 $8',
        },
      },
    },
  },
});
SidebarTargetStack.displayName = 'SidebarTargetStack';

const SidebarTargetContainer = styled('div', {
  position: 'relative',
  zIndex: stateZIndexes.initial,
  display: 'flex',
  borderRadius: '$8',

  $$sidebarTargetMediaColor: colors.iconNeutralLight,
  $$sidebarTargetLabelColor: colors.bodyNeutralLight,
  $$sidebarTargetArrowsColor: colors.iconNeutralLight,

  [darkThemeSelector]: {
    $$sidebarTargetMediaColor: colors.iconNeutralDark,
    $$sidebarTargetLabelColor: colors.bodyNeutralDark,
    $$sidebarTargetArrowsColor: colors.iconNeutralDark,
  },

  variants: {
    arrangement: {
      'hidden-label': {},
      'leading-icon': {
        width: '100%',
        minWidth: 0,
      },
    },
    internal: {
      true: {},
      false: {},
    },
    isPressable: {
      true: {
        cursor: 'pointer',
        userSelect: 'none',
      },
      false: {
        cursor: 'default',
      },
    },
    minimized: {
      true: {},
      false: {},
    },
    selected: {
      true: {},
      false: {},
    },
  },

  compoundVariants: [
    {
      arrangement: 'hidden-label',
      minimized: false,
      css: {
        width: 'min-content',
      },
    },
    {
      isPressable: true,
      css: {
        [selectors.interactive]: {
          zIndex: stateZIndexes.hover,
          backgroundColor: colors.bgNeutralLight,

          $$sidebarTargetMediaColor: colors.bodyNeutralLight,
          $$sidebarTargetLabelColor: colors.headingNeutralLight,
          $$sidebarTargetArrowsColor: colors.iconNeutralLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgNeutralDark,

            $$sidebarTargetMediaColor: colors.bodyNeutralDark,
            $$sidebarTargetLabelColor: colors.headingNeutralDark,
            $$sidebarTargetArrowsColor: colors.iconNeutralDark,
          },
        },

        [selectors.focus]: {
          zIndex: stateZIndexes.focus,
          outline: 'none',
          boxShadow: shadows.focusRingLight,

          $$sidebarTargetMediaColor: colors.bodyBrandLight,
          $$sidebarTargetLabelColor: colors.headingBrandLight,
          $$sidebarTargetArrowsColor: colors.iconBrandLight,

          [darkThemeSelector]: {
            boxShadow: shadows.focusRingDark,

            $$sidebarTargetMediaColor: colors.bodyBrandDark,
            $$sidebarTargetLabelColor: colors.headingBrandDark,
            $$sidebarTargetArrowsColor: colors.iconBrandDark,
          },
        },
      },
    },
    {
      isPressable: true,
      selected: true,
      css: {
        zIndex: stateZIndexes.selected,

        [selectors.all]: {
          backgroundColor: colors.bgBrandLight,
          strokeAll: colors.strokeBrandLight,

          $$sidebarTargetMediaColor: colors.bodyBrandLight,
          $$sidebarTargetLabelColor: colors.headingBrandLight,
          $$sidebarTargetArrowsColor: colors.iconBrandLight,

          [darkThemeSelector]: {
            backgroundColor: colors.bgBrandDark,
            strokeAll: colors.strokeBrandDark,

            $$sidebarTargetMediaColor: colors.bodyBrandDark,
            $$sidebarTargetLabelColor: colors.headingBrandDark,
            $$sidebarTargetArrowsColor: colors.iconBrandDark,
          },
        },
      },
    },
    {
      internal: true,
      css: {
        $$sidebarTargetMediaColor: colors.internalIconLight,
        $$sidebarTargetLabelColor: colors.internalBodyLight,
        $$sidebarTargetArrowsColor: colors.internalIconLight,

        [darkThemeSelector]: {
          $$sidebarTargetMediaColor: colors.internalIconDark,
          $$sidebarTargetLabelColor: colors.internalBodyDark,
          $$sidebarTargetArrowsColor: colors.internalIconDark,
        },

        [selectors.interactive]: {
          backgroundColor: colors.internalBgLight,

          $$sidebarTargetMediaColor: colors.internalBodyLight,
          $$sidebarTargetLabelColor: colors.internalHeadingLight,
          $$sidebarTargetArrowsColor: colors.internalIconLight,

          [darkThemeSelector]: {
            backgroundColor: colors.internalBgDark,

            $$sidebarTargetMediaColor: colors.internalBodyDark,
            $$sidebarTargetLabelColor: colors.internalHeadingDark,
            $$sidebarTargetArrowsColor: colors.internalIconDark,
          },
        },
      },
    },
    {
      internal: true,
      isPressable: true,
      selected: true,
      css: {
        [selectors.all]: {
          backgroundColor: colors.internalBgLight,
          strokeAll: colors.internalStrokeLight,

          $$sidebarTargetMediaColor: colors.internalBodyLight,
          $$sidebarTargetLabelColor: colors.internalHeadingLight,
          $$sidebarTargetArrowsColor: colors.internalIconLight,

          [darkThemeSelector]: {
            backgroundColor: colors.internalBgDark,
            strokeAll: colors.internalStrokeDark,

            $$sidebarTargetMediaColor: colors.internalBodyDark,
            $$sidebarTargetLabelColor: colors.internalHeadingDark,
            $$sidebarTargetArrowsColor: colors.internalIconDark,
          },
        },
      },
    },
  ],
});
SidebarTargetContainer.displayName = 'SidebarTargetContainer';

export function SidebarTarget({
  accessLevel: accessLevelProp,
  annotations,
  arrangement = 'leading-icon',
  arrows,
  callout,
  composeProps,
  id,
  internal = false,
  isOpen,
  label,
  media,
  minimized,
  selected,
  suppressMinimizedTooltip,
  keepMenuOpenOnNavigate = false,
  // Routing
  as,
  onClick,
  to,
  ref,
  ...remaining
}: SidebarTargetProps) {
  const canHover = useCanHover();
  const { minimized: sidebarMinimized, menuOpen, setMenuOpen, isNarrow } = useSidebarContext();
  const effectiveMinimized = minimized ?? sidebarMinimized;
  const accessLevel = useAccessLevelContext(accessLevelProp);
  const isLink = Boolean(as && to);
  const isPressable = Boolean(onClick || as);
  const closeMenuAfterNav =
    isLink && isNarrow && menuOpen && !keepMenuOpenOnNavigate
      ? (event: MouseEvent<HTMLElement>) => {
          // Tree roots call preventDefault when toggling branches instead of navigating.
          if (!event.defaultPrevented) setMenuOpen(false);
        }
      : undefined;
  const rootRole = isPressable && !isLink ? 'button' : undefined;
  const showAccessBadge = Boolean(accessLevel && accessLevel !== AccessLevel.Public);
  const hasEnd =
    arrangement !== 'hidden-label' &&
    !effectiveMinimized &&
    (Boolean(annotations) || Boolean(arrows) || showAccessBadge);

  const {
    ref: composeRef,
    onClick: composeOnClick,
    onMouseEnter: composeOnMouseEnter,
    onFocus: composeOnFocus,
    onBlur: composeOnBlur,
    onKeyDown: composeOnKeyDown,
    onPointerEnter: composeOnPointerEnter,
    onPointerDown: composeOnPointerDown,
    tabIndex: composeTabIndex,
    ...composeRest
  } = composeProps ?? {};

  const render = (
    <SidebarTargetContainer
      {...composeRest}
      {...remaining}
      id={id}
      aria-label={
        arrangement === 'hidden-label' ||
        (effectiveMinimized && !canHover && !suppressMinimizedTooltip)
          ? label
          : undefined
      }
      role={rootRole}
      tabIndex={composeTabIndex ?? (isPressable && !selected ? 0 : -1)}
      aria-current={isLink && selected ? 'page' : undefined}
      as={as}
      to={to}
      ref={mergeRefs(
        [ref, composeRef].filter(Boolean) as Parameters<typeof mergeRefs<HTMLElement>>[0],
      )}
      onClick={mergeHandler(composeOnClick, mergeHandler(onClick, closeMenuAfterNav))}
      onMouseEnter={mergeHandler(composeOnMouseEnter, undefined)}
      onFocus={mergeHandler(composeOnFocus, undefined)}
      onBlur={mergeHandler(composeOnBlur, undefined)}
      onKeyDown={mergeHandler(composeOnKeyDown, undefined)}
      onPointerEnter={mergeHandler(composeOnPointerEnter, undefined)}
      onPointerDown={mergeHandler(composeOnPointerDown, undefined)}
      arrangement={arrangement}
      internal={internal || accessLevel === AccessLevel.Internal}
      isPressable={isPressable}
      minimized={effectiveMinimized}
      selected={selected}
    >
      {(arrangement === 'hidden-label' || effectiveMinimized) && (
        <SidebarTargetAnnotationsBadges accessLevel={null} badges={annotations?.badges} bubbled />
      )}
      {callout && <Callout position={{ top: 6, right: 6 }} {...callout} />}
      <SidebarTargetStack
        minimized={effectiveMinimized}
        direction="row"
        justify={effectiveMinimized ? 'center' : 'start'}
        gap={arrangement === 'hidden-label' ? 4 : 8}
        preset="body"
        width={arrangement === 'hidden-label' && !effectiveMinimized ? 'min-content' : '100%'}
        start={media && <SidebarTargetMedia minimized={effectiveMinimized} {...media} />}
        end={
          hasEnd && (
            <SidebarTargetEnd>
              {(annotations || showAccessBadge) && (
                <SidebarTargetAnnotations accessLevel={accessLevel} {...(annotations ?? {})} />
              )}
              {arrows && <SidebarTargetArrows type={arrows} isOpen={isOpen} />}
            </SidebarTargetEnd>
          )
        }
      >
        {arrangement !== 'hidden-label' && !effectiveMinimized && (
          <SidebarTargetLabel truncate>{label}</SidebarTargetLabel>
        )}
      </SidebarTargetStack>
    </SidebarTargetContainer>
  );

  const showTooltip =
    canHover &&
    ((effectiveMinimized && !suppressMinimizedTooltip) || arrangement === 'hidden-label');

  if (showTooltip) {
    return (
      <Tooltip
        side={effectiveMinimized ? 'right' : 'bottom'}
        sideOffset={4}
        contents={
          <AlignStack
            align="center"
            gap={4}
            wrap="nowrap"
            preset="small"
            end={
              (annotations || showAccessBadge) && (
                <SidebarTargetAnnotations accessLevel={accessLevel} {...(annotations ?? {})} />
              )
            }
          >
            <Text>{label}</Text>
          </AlignStack>
        }
      >
        {render}
      </Tooltip>
    );
  }

  return render;
}
